import { ref, reactive, computed, onMounted, onUnmounted, getCurrentInstance, nextTick, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import bus from 'vue3-eventbus';
import { wexinShare, getWechatShareImgUrl } from '@/utils/wxshare.js';
import { navigateNewIndexSearch } from '@/utils/newIndexSearch.js';
import { formatDisplayName } from '@/utils/phoneVerify.js';
import {
  mapIndexNewsList,
  mapIndexSliderList,
  mapHotPicFlatList,
  mapSubjectCatList,
  mapCuratedArticle,
  mapInternationalIndex,
  mapAskQingdaoIndex,
  mapActivitiesList,
  mapActivitiesToSpotCards,
  ACTIVITY_CHANNEL_STORY,
  ACTIVITY_CHANNEL_COLLECT,
} from '@/utils/newIndexMappers';
import { getDailyScene, getWeeklyPick, getAskQingdaoIndex, getPortalFloatAd, getPortalActivitySection } from '@/api/common';
import { sloganScenes as mockSloganScenes, newsList as mockNewsList, spotCards as mockSpotCards, askQingdaoIndexMock } from '@/data/newIndexMock.js';
import { USE_NEW_INDEX_MOCK, SHOW_ASK_QINGDAO_SECTION } from '@/config/newIndexDataSource.js';
import { usePortalNav } from '@/composables/usePortalNav.js';
import { scrollNewIndexToHash } from '@/utils/newIndexScroll.js';
import PageLoading from '@/components/common/PageLoading.vue';
import NewIndexHeaderV2 from '@/components/newIndex/NewIndexHeaderV2.vue';
import NewsCarousel from '@/components/newIndex/NewsCarousel.vue';
import SloganSlider from '@/components/newIndex/SloganSlider.vue';
import SpotsGrid from '@/components/newIndex/SpotsGrid.vue';
import Activities from '@/components/newIndex/Activities.vue';
import IntCommunicator from '@/components/newIndex/IntCommunicator.vue';
import PhotoDepot from '@/components/newIndex/PhotoDepot.vue';
import RecommendedPhotos from '@/components/newIndex/RecommendedPhotos.vue';
import AskQingdao from '@/components/newIndex/AskQingdao.vue';
import FloatAd from '@/components/newIndex/FloatAd.vue';
import Footer from '@/components/layout/footer.vue';
import SideComponent from '@/components/common/SideComponent.vue';

export default {
  name: 'new_index',
  components: {
    PageLoading,
    SideComponent,
    NewIndexHeaderV2,
    NewsCarousel,
    SloganSlider,
    SpotsGrid,
    Activities,
    IntCommunicator,
    PhotoDepot,
    RecommendedPhotos,
    AskQingdao,
    FloatAd,
    Footer,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const { proxy } = getCurrentInstance();
    const pageRoot = ref(null);
    const { navTree, ensureNav } = usePortalNav();
    const floatAd = ref(null);

    const showDialog = ref(false);
    const showLogin = ref(false);
    const showMobile = ref(false);

    const checkLoggedIn = () => {
      const isAuthenticated = proxy.$cookies.getAuthKey();
      const token = proxy.$cookies.getToken();
      return !!(
        isAuthenticated &&
        token !== undefined &&
        token !== '' &&
        token != null
      );
    };

    const isLoggedIn = computed(() => checkLoggedIn());

    const username = computed(() => {
      if (!checkLoggedIn()) return '';
      const info = store.state.user.userInfo || {};
      return formatDisplayName(info.username, info.mobile, t('anonymity'));
    });

    const onLoginDialogChange = (value) => {
      showDialog.value = value.value;
    };

    const openLogin = () => {
      sessionStorage.setItem('redirectPath', route.fullPath);
      showDialog.value = true;
      showMobile.value = false;
      showLogin.value = true;
    };

    const onShowLoginDialog = (e) => { showDialog.value = e; };
    const onShowLoginDialogMobile = (e) => { showMobile.value = e; };
    const onShowLoginDialogLogin = (e) => { showLogin.value = e; };

    const pageData = reactive({
      newsList: [],
      sloganScenes: [],
      spotCards: [],
      activities: [],
      hotPhotos: [],
      recommendedHotPhotos: [],
      subjectCats: [],
      heroSliders: [],
      logoUrl: '',
      dailyScene: null,
      weeklyPick: null,
      internationalItems: [],
      askQingdao: {
        section: { title: '', subtitle: '' },
        items: [],
      },
      loading: true,
    });

    const wxshare = (shareData) => {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      let url = `${protocol}//${hostname}`;
      if (hostname.indexOf('www') === -1) {
        url = `${protocol}//www.${hostname}`;
      }
      url += route.fullPath;

      store
        .dispatch('common/Jssdk', { url })
        .then((res) => {
          wexinShare(res, {
            title: (shareData.title || '').replace('<br>', ' '),
            desc: shareData.seo_description || '',
            link: url,
            imgUrl: getWechatShareImgUrl(shareData),
          });
        })
        .catch(() => {});
    };

    const loadPageData = async () => {
      pageData.loading = true;
      let siteRes = null;

      const applyInternationalIndex = (res) => {
        const mapped = mapInternationalIndex(res);
        if (mapped.items.length) {
          pageData.internationalItems = mapped.items;
        }
        return mapped;
      };

      const siteInfoPromise = store.dispatch('common/fetchSiteInfo').then((res) => {
        siteRes = res;
        pageData.heroSliders = Array.isArray(res?.sliders) ? res.sliders : [];
        pageData.logoUrl = res?.logo_white || res?.logo || '';
        wxshare(res);
        return res;
      });

      const hotImagesPromise = store
        .dispatch('common/getHotImages', { limit: 40 })
        .then((res) => {
          const list = Array.isArray(res) ? res : [];
          pageData.recommendedHotPhotos = list;
          pageData.hotPhotos = mapHotPicFlatList(list);
        })
        .catch(() => {
          pageData.recommendedHotPhotos = [];
          pageData.hotPhotos = [];
        });

      const internationalPromise = store
        .dispatch('common/InternationalIndex', { size: 6 })
        .then((res) => applyInternationalIndex(res))
        .catch(() => null);

      const newsPromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.newsList = mockNewsList;
        })
        : store
          .dispatch('common/GetIndexNews', { size: 10, type: 1 })
          .then((res) => {
            pageData.newsList = mapIndexNewsList(res);
          })
          .catch(() => {
            pageData.newsList = [];
          });

      const sliderPromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.sloganScenes = mockSloganScenes;
        })
        : store
          .dispatch('common/GetIndexSlider')
          .then((res) => {
            const mapped = mapIndexSliderList(res);
            pageData.sloganScenes = mapped.length ? mapped : mockSloganScenes;
          })
          .catch(() => {
            pageData.sloganScenes = mockSloganScenes;
          });

      const shanhaiPromise = USE_NEW_INDEX_MOCK
        ? Promise.resolve().then(() => {
          pageData.spotCards = mockSpotCards;
        })
        : getPortalActivitySection({ channel: ACTIVITY_CHANNEL_STORY })
          .then((res) => {
            pageData.spotCards = mapActivitiesToSpotCards(res, 4);
          })
          .catch(() => {
            pageData.spotCards = [];
          });

      const activitiesPromise = getPortalActivitySection({ channel: ACTIVITY_CHANNEL_COLLECT })
        .then((res) => {
          pageData.activities = mapActivitiesList(res, 2);
        })
        .catch(() => {
          pageData.activities = [];
        });

      const subjectCatsPromise = store
        .dispatch('common/getSubjectCats', { pid: 0 })
        .then((res) => {
          pageData.subjectCats = mapSubjectCatList(res);
        })
        .catch(() => {
          pageData.subjectCats = [];
        });

      const dailyScenePromise = getDailyScene()
        .then((res) => {
          pageData.dailyScene = mapCuratedArticle(res, 'daily-scene');
        })
        .catch(() => {
          pageData.dailyScene = null;
        });

      const weeklyPickPromise = getWeeklyPick()
        .then((res) => {
          pageData.weeklyPick = mapCuratedArticle(res, 'weekly-pick');
        })
        .catch(() => {
          pageData.weeklyPick = null;
        });

      const navPromise = ensureNav();

      const floatAdPromise = getPortalFloatAd()
        .then((res) => {
          const list = Array.isArray(res) ? res : [];
          floatAd.value = list.find((item) => item && item.image) || null;
        })
        .catch(() => {
          floatAd.value = null;
        });

      const askQingdaoPromise = SHOW_ASK_QINGDAO_SECTION
        ? getAskQingdaoIndex()
          .then((res) => {
            pageData.askQingdao = mapAskQingdaoIndex(res);
          })
          .catch(() => {
            pageData.askQingdao = { section: { title: '', subtitle: '' }, items: [] };
          })
        : Promise.resolve();

      const pagePromises = [
        siteInfoPromise,
        hotImagesPromise,
        internationalPromise,
        newsPromise,
        sliderPromise,
        shanhaiPromise,
        activitiesPromise,
        subjectCatsPromise,
        dailyScenePromise,
        weeklyPickPromise,
        navPromise,
        floatAdPromise,
      ];

      if (SHOW_ASK_QINGDAO_SECTION) {
        pagePromises.push(askQingdaoPromise);
      }

      await Promise.all(pagePromises);

      if (SHOW_ASK_QINGDAO_SECTION && !pageData.askQingdao.items.length) {
        pageData.askQingdao = {
          section: { ...askQingdaoIndexMock.section },
          items: [...askQingdaoIndexMock.items],
        };
      }

      if (!pageData.internationalItems.length) {
        const fallback = mapInternationalIndex(siteRes?.international_index || {});
        if (fallback.items.length) {
          pageData.internationalItems = fallback.items;
        }
      }

      pageData.loading = false;
      await scrollToRouteHashIfNeeded();
    };

    const scrollToRouteHashIfNeeded = async () => {
      if (!route.hash) return;

      await nextTick();
      requestAnimationFrame(() => {
        if (!scrollNewIndexToHash(route.hash)) {
          requestAnimationFrame(() => scrollNewIndexToHash(route.hash));
        }
      });
    };

    const handleSearch = (payload) => {
      const query = typeof payload === 'string' ? payload : payload?.query;
      const category = payload?.category || 'news';
      navigateNewIndexSearch(router, store, { query, category });
    };

    const getScrollElement = () => {
      const page = pageRoot.value;
      if (page && page.scrollHeight > page.clientHeight + 1) {
        return page;
      }
      return document.scrollingElement || document.documentElement;
    };

    const scroll = (val) => {
      const el = getScrollElement();
      const viewport = el.clientHeight;
      const maxScroll = Math.max(0, el.scrollHeight - viewport);
      const current = el.scrollTop;

      if (val === 'top') {
        el.scrollTo({
          top: Math.max(0, current - viewport),
          behavior: 'smooth',
        });
      } else if (val === 'bottom') {
        el.scrollTo({
          top: Math.min(maxScroll, current + viewport),
          behavior: 'smooth',
        });
      }
    };

    onMounted(() => {
      sessionStorage.setItem('redirectPath', route.fullPath);
      bus.on('showLoginDialog', onShowLoginDialog);
      bus.on('showLoginDialogMobile', onShowLoginDialogMobile);
      bus.on('showLoginDialogLogin', onShowLoginDialogLogin);
      bus.emit('showFooter', true);
      loadPageData();
    });

    watch(
      () => route.hash,
      () => {
        scrollToRouteHashIfNeeded();
      }
    );

    onUnmounted(() => {
      bus.off('showLoginDialog', onShowLoginDialog);
      bus.off('showLoginDialogMobile', onShowLoginDialogMobile);
      bus.off('showLoginDialogLogin', onShowLoginDialogLogin);
      bus.emit('showFooter', false);
    });

    return {
      pageRoot,
      navTree,
      floatAd,
      pageData,
      handleSearch,
      scroll,
      showDialog,
      showLogin,
      showMobile,
      isLoggedIn,
      username,
      openLogin,
      onLoginDialogChange,
      showAskQingdaoSection: SHOW_ASK_QINGDAO_SECTION,
    };
  },
};
